import { useNavigate } from "react-router-dom";
import {
  ChartCard, TrendArea, RankedBars, Meter, DataTable, ChartEmpty,
  inr, num, INK, ONE, SERIES, ACCENT,
} from "./chartKit";

/**
 * The dashboard's existing sections, plotted.
 *
 * Nothing new is fetched here — this reads the same `/api/dashboard` payload the
 * card view reads, and answers the same questions in the form that suits them.
 * Two views over one response, so the two can never disagree.
 */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** The payment-list filter a revenue point links to. */
const monthRange = (m) => {
  const idx = MONTHS.indexOf(m.month);
  const year = Number(m.year);
  if (idx < 0 || !year) return null;
  const mm = String(idx + 1).padStart(2, "0");
  return {
    fromDate: `${year}-${mm}-01`,
    toDate: `${year}-${mm}-${new Date(year, idx + 1, 0).getDate()}`,
  };
};

const PLAN_LABEL = (name = "") => name.replace(" Plan", "");

const MODE_LABEL = {
  upi: "UPI", cash: "Cash", bank_transfer: "Bank Transfer", internal: "Internal",
  card: "Card", cheque: "Cheque", netbanking: "Net Banking",
};

export default function DashboardCharts({ data }) {
  const navigate = useNavigate();

  const rev = data?.revenue || {};
  const plans = data?.plan_distribution || [];
  const subs = data?.subscription_types || {};
  const pmodes = data?.payment_modes || [];
  const seg = data?.customer_segments || {};
  const tm = data?.trademark_monitoring || {};

  const monthly = (rev.monthly_breakdown || []).map((m) => ({
    label: `${m.month} ${String(m.year).slice(-2)}`,
    amount: Number(m.amount || 0),
    range: monthRange(m),
  }));

  const planBars = plans.map((p) => ({
    name: PLAN_LABEL(p.plan_name),
    value: Number(p.count || 0),
    plan_name: p.plan_name,
    percentage: p.percentage,
  }));

  /* Named in the order the cards name them, not by size: this is the same three
     buckets every time, and a row that reorders itself between refreshes is
     harder to read than one that does not. */
  const typeBars = [
    { name: "Annually", value: Number(subs.annual || 0), plan_type: "annually" },
    { name: "Monthly", value: Number(subs.monthly || 0), plan_type: "monthly" },
    { name: "Free Trial", value: Number(subs.free_trial || 0), plan_type: "free_trial" },
  ];

  const modeBars = pmodes.map((p) => ({
    name: MODE_LABEL[p.mode] || p.mode,
    value: Number(p.count || 0),
    // The stored value, which is what the payment list filters on. The label
    // above is for reading; "Bank Transfer" would match nothing.
    mode: p.mode,
  }));

  const segBars = [
    { name: "Attorneys", value: Number(seg.attorneys || 0), is_attorney: 1 },
    { name: "Corporates", value: Number(seg.corporates || 0), is_attorney: 0 },
  ];

  const stateBars = (seg.top_states || []).map((s) => ({
    name: (s.state || "—").replace(/\b\w/g, (c) => c.toUpperCase()),
    value: Number(s.count || 0),
    /* Title Case is for the axis; the filter carries the value as stored, so a
       row reading "Up" still finds the customers saved under "up".
       "Others" is the API's bucket for everything outside the top few — it is
       not a state, so it gets nothing to click through to. */
    state: /^others$/i.test(s.state || "") ? null : s.state,
  }));

  /* One destination for both the plot and the table beneath it. A month with
     no usable year cannot be turned into a date range, so it simply does not
     navigate rather than opening the list unfiltered. */
  const openMonth = (row) => {
    if (row?.range) navigate("/cms/payment-list", { state: row.range });
  };

  const openMode = (b) => {
    const mode = b?.payload?.mode;
    if (mode) navigate("/cms/payment-list", { state: { paymentMode: mode } });
  };

  const openState = (b) => {
    const st = b?.payload?.state;
    if (st) navigate("/cms/manage-customer", { state: { customer_state: st } });
  };

  return (
    <>
      {/* ── Revenue over time ── */}
      <ChartCard
        title="Monthly Revenue"
        hint="collected per month · click any month to open its payments"
        accent={ACCENT.purple}
        style={{ marginBottom: 18 }}
      >
        <TrendArea data={monthly} onPointClick={openMonth} />
        <div style={{ marginTop: 14 }}>
          <DataTable
            maxHeight={180}
            rows={[...monthly].reverse()}
            onRowClick={openMonth}
            columns={[
              { key: "label", title: "Month" },
              { key: "amount", title: "Collected", align: "right", render: (r) => inr(r.amount) },
            ]}
          />
        </div>
      </ChartCard>

      {/* ── Trademark monitoring: three numbers, two of them shares of the first ──
             Shares, so meters rather than three bars of their own — the question
             is what proportion of the registry is distinct and monitored. */}
      <ChartCard title="Trademark Monitoring (Customers)" accent={ACCENT.amber} style={{ marginBottom: 18 }}>
        {Number(tm.total_trademarks) > 0 ? (
          <>
            <div style={{ fontSize: 30, fontWeight: 700, color: INK.strong, letterSpacing: -0.6, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
              {num(tm.total_trademarks)}
            </div>
            <div style={{ fontSize: 11.5, color: INK.muted, margin: "5px 0 18px" }}>
              total trademark records
            </div>
            <Meter label="Distinct trademarks" value={tm.total_distinct_trademarks} total={tm.total_trademarks} />
            <Meter label="Under monitoring (paid + trial)" value={tm.total_monitoring} total={tm.total_trademarks} color={SERIES[2]} />
          </>
        ) : <ChartEmpty height={160} />}
      </ChartCard>

      {/* ── Three distributions, side by side ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 18, marginBottom: 18 }}>
        <ChartCard title="Plan Distribution" hint="customers per plan" accent={ACCENT.purple}>
          <RankedBars
            data={planBars}
            fmt={num}
            nameWidth={78}
            onBarClick={(b) => b?.payload?.plan_name
              && navigate("/cms/manage-customer", { state: { plan_name: b.payload.plan_name } })}
            extra={(p) => (p?.percentage != null ? `${p.percentage}% of customers` : "")}
          />
        </ChartCard>

        <ChartCard title="Plan Types" hint="active subscriptions" accent={ACCENT.blue}>
          <RankedBars
            data={typeBars}
            fmt={num}
            nameWidth={78}
            onBarClick={(b) => b?.payload?.plan_type
              && navigate("/cms/manage-customer", { state: { plan_type: b.payload.plan_type } })}
          />
        </ChartCard>

        <ChartCard title="Payment Modes" hint="transactions by mode · click to see them" accent={ACCENT.green}>
          <RankedBars data={modeBars} fmt={num} nameWidth={96} onBarClick={openMode} />
        </ChartCard>
      </div>

      {/* ── Segments ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 18 }}>
        <ChartCard title="Customer Segments" hint="click a bar to filter the customer list" accent={ACCENT.teal}>
          <RankedBars
            data={segBars}
            fmt={num}
            nameWidth={86}
            /* Attorney and corporate are the two identities this dashboard is
               organised around, so they keep their hues here and everywhere
               else on the screen. */
            colorOf={(_, i) => SERIES[i]}
            onBarClick={(b) => b?.payload
              && navigate("/cms/manage-customer", { state: { is_attorney: b.payload.is_attorney } })}
          />
          <div style={{ marginTop: 6 }}>
            <Meter
              label="Registered with GST"
              value={seg.gst_registered_percentage ?? 0}
              total={100}
              color={SERIES[2]}
              fmt={(v) => `${Number(v || 0)}%`}
            />
          </div>
        </ChartCard>

        <ChartCard title="Top States" hint="customers by state · click to see them" accent={ACCENT.orange}>
          <RankedBars data={stateBars} fmt={num} nameWidth={118} color={ONE} onBarClick={openState} />
        </ChartCard>
      </div>
    </>
  );
}
